// @ts-check

/**
 * Size Guide — PDP modal driven by the product's size guide metaobject
 * (custom.size_guide_metaobject). See blocks/size-guide.liquid.
 *
 * Progressive enhancement only: the server renders the full guide with the
 * default size already visible (matched to the selected variant where the
 * product has a Size option). This component:
 *   - swaps the visible media/pill when a size pill is clicked;
 *   - re-syncs itself from the section HTML the variant picker fetches on
 *     variant change (StandardEvents.productSelect + morph — the same
 *     pattern as product-inventory/sku), so the "your size" dot and the
 *     default open size follow the selected variant without a page load.
 * Open/close is inherited from the theme's DialogComponent.
 */

import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';

/**
 * @typedef {object} SizeGuideRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement[]} [pills]
 * @property {HTMLElement[]} [media]
 */

class SizeGuideComponent extends DialogComponent {
  /** @type {Element | null} */
  #section = null;

  /**
   * Zoom srcs already warmed by #preloadZoom, so hovering the same trigger
   * repeatedly doesn't queue duplicate requests.
   *
   * @type {Set<string>}
   */
  #preloaded = new Set();

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('pointerover', this.#preloadZoom);
    this.addEventListener('focusin', this.#preloadZoom);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
    this.removeEventListener('pointerover', this.#preloadZoom);
    this.removeEventListener('focusin', this.#preloadZoom);
  }

  /**
   * Warms the browser cache for a zoom image as soon as the pointer or
   * keyboard focus reaches its trigger, so openZoom paints straight away
   * instead of showing an empty modal while the 1800px file downloads.
   * Fire-and-forget: if it hasn't finished by the time the dialog opens,
   * the in-flight request is reused rather than restarted.
   *
   * @param {Event} event - pointerover / focusin from anywhere in the block.
   */
  #preloadZoom = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const trigger = target?.closest('[data-zoom-src]');

    if (!(trigger instanceof HTMLElement)) return;

    const src = trigger.dataset.zoomSrc;

    if (!src || this.#preloaded.has(src)) return;

    this.#preloaded.add(src);

    const warm = new Image();
    warm.decoding = 'async';
    warm.src = src;
  };

  /**
   * Switches the previewed size to the pill that was clicked.
   *
   * @param {Event} event - Click event from a pill (declarative `on:click="/selectSize"`).
   */
  selectSize(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const size = target?.dataset.size;

    if (!size) return;

    const refs = /** @type {SizeGuideRefs} */ (/** @type {unknown} */ (this.refs));
    const { pills = [], media = [] } = refs;

    for (const pill of pills) {
      const active = pill.dataset.size === size;
      pill.classList.toggle('size-guide__pill--active', active);
      pill.setAttribute('aria-pressed', String(active));
    }

    for (const item of media) {
      item.hidden = item.dataset.size !== size;
    }
  }

  /**
   * Opens the stacked zoom dialog with the clicked image at high
   * resolution. The large image only loads on first zoom — src is set
   * here from the trigger's data-zoom-src, never server-rendered.
   *
   * @param {Event} event - Click event from a zoom trigger (declarative `on:click="/openZoom"`).
   */
  openZoom(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const trigger = target?.closest('[data-zoom-src]');
    const zoom = /** @type {any} */ (this.refs).zoom;

    if (!(trigger instanceof HTMLElement) || !(zoom instanceof HTMLElement)) return;

    const image = zoom.querySelector('.size-guide__zoom-image');

    if (image instanceof HTMLImageElement && trigger.dataset.zoomSrc) {
      if (image.src !== trigger.dataset.zoomSrc) {
        image.src = trigger.dataset.zoomSrc;
      }
      image.alt = trigger.dataset.zoomAlt ?? '';
    }

    customElements.upgrade(zoom);

    if (typeof (/** @type {any} */ (zoom).showDialog) === 'function') {
      /** @type {any} */ (zoom).showDialog();
    }
  }

  /**
   * Re-renders this block from the updated section HTML fetched by the
   * variant picker, keeping the "your size" dot and default open size in
   * sync with the selected variant.
   *
   * @param {any} event - ProductSelectEvent (StandardEvents.productSelect).
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      ?.then((/** @type {any} */ result) => {
        const html = result?.detail?.html;
        if (!html) return;

        const next = html.querySelector('size-guide-component');
        if (!next) return;

        morph(this, next);
      })
      .catch(() => {});
  };
}

if (!customElements.get('size-guide-component')) {
  customElements.define('size-guide-component', SizeGuideComponent);
}
